import { service } from '@ember/service';
import { Ability } from 'ember-can';
import type AbilitiesService from 'ember-can/services/abilities';
import config from 'garaje/config/environment';
import type AuthzService from 'garaje/services/authz';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { Permission } from 'garaje/utils/ui-permissions';

export default class ManageSettingsAbility extends Ability {
  @service declare abilities: AbilitiesService;
  @service declare authz: AuthzService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare state: StateService;

  get canVisitLocal(): boolean {
    return (
      this.canVisitLocalAdmins ||
      this.canVisitLocalCustomRoles ||
      this.canUpdateLocalSettings ||
      this.canVisitLocalEventLog
    );
  }

  get canVisitLocalAdmins(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.USER_MANAGEMENT_ADMIN_READ);
  }

  get canVisitLocalCustomRoles(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.USER_MANAGEMENT_CUSTOM_ROLE_READ);
  }

  get canUpdateLocalSettings(): boolean {
    // Aggregate permissions from feature-flagged permission sets.
    // If neither of the following flags are enabled, use legacy roles.
    // - location-authz-permissions: account management location update permission
    // - visitors-permission-manage-location-settings: granular location config permissions
    let permissions: Permission[] = [];

    if (this.featureFlags.isEnabled('location-authz-permissions')) {
      permissions = [...permissions, Permission.ACCOUNT_MANAGEMENT_LOCATION_UPDATE];
    }

    if (this.featureFlags.isEnabled('visitors-permission-manage-location-settings')) {
      permissions = [
        ...permissions,
        Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_ADDRESS,
        Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_BRANDING,
        Permission.VISITORS_LOCATION_CONFIGURATION_GENERATE_KEY,
        Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_LANGUAGE,
        Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_DISABLED,
        Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_CAPACITY,
        Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_WORKPLACE_UTILIZATION,
        Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_SIGN_OUT,
        Permission.VISITORS_GDPR_CONFIGURATION_CREATE,
        Permission.VISITORS_GDPR_CONFIGURATION_UPDATE,
        Permission.VISITORS_GDPR_CONFIGURATION_DELETE,
      ];
    }

    return permissions.length === 0
      ? this.abilities.can('edit location-setting')
      : this.authz.hasAnyPermissionAtCurrentLocation(permissions);
  }

  get canVisitLocalEventLog(): boolean {
    return Boolean(
      !this.#isEURegion() &&
        this.state.features?.canAccessEventLog &&
        this.featureFlags.isEnabled('audit-log') &&
        this.authz.hasPermissionAtCurrentLocation(Permission.USER_MANAGEMENT_ACTIVITY_LOG_READ),
    );
  }

  get canVisitGlobal(): boolean {
    return (
      this.canVisitAccountSettings ||
      this.canVisitGlobalAdmins ||
      this.canVisitGlobalCustomRoles ||
      this.canVisitGlobalEventLog
    );
  }

  get canVisitAccountSettings(): boolean {
    const canAccessAccountCompanySettings = this.authz.hasPermissionAtCurrentCompany(
      Permission.ACCOUNT_MANAGEMENT_COMPANY_CONFIG_UPDATE,
    );
    const canAccessAccountProductSettings = this.authz.hasPermissionAtCurrentCompany(
      Permission.ACCOUNT_MANAGEMENT_PRODUCT_SETTINGS_READ,
    );

    return (this.canAccessInactivityLogout && canAccessAccountCompanySettings) || canAccessAccountProductSettings;
  }

  get canVisitGlobalAdmins(): boolean {
    return this.authz.hasPermissionAtCurrentCompany(Permission.USER_MANAGEMENT_ADMIN_READ);
  }

  get canVisitGlobalCustomRoles(): boolean {
    return this.authz.hasPermissionAtCurrentCompany(Permission.USER_MANAGEMENT_CUSTOM_ROLE_READ);
  }

  get canVisitGlobalEventLog(): boolean {
    return Boolean(
      !this.#isEURegion() &&
        this.state.features?.canAccessEventLog &&
        this.featureFlags.isEnabled('audit-log') &&
        this.authz.hasPermissionAtCurrentCompany(Permission.USER_MANAGEMENT_ACTIVITY_LOG_READ),
    );
  }

  get canAccessInactivityLogout(): boolean {
    return !!this.state.features?.canAccessInactivityLogout;
  }

  #isEURegion(): boolean {
    return config.environment === 'eu';
  }
}
